import React, { useState } from 'react';
import { Button } from './ui/button';
import { X, Gift, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import { i18n } from '../utils/i18n';

interface WelcomePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onWelcomeBonusClick: () => void;
  onDontShowAgain: () => void;
}

export function WelcomePopup({ isOpen, onClose, onWelcomeBonusClick, onDontShowAgain }: WelcomePopupProps) {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleDontShowAgain = () => {
    onDontShowAgain();
    handleClose();
    toast.success('이제 이 메시지가 표시되지 않습니다.');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl transform transition-all duration-300 ${
        isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
      }`}>
        {/* Header - WelcomeBonus 배너 스타일 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">{i18n.t('welcomeBonus')}</h2>
              <p className="text-sm text-gray-600">{i18n.t('registrationComplete')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* 축하금 안내 */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">30</span>
              </div>
              <div>
                <p className="font-semibold text-blue-800">{i18n.t('registrationBonus')}</p>
                <p className="text-sm text-blue-600">{i18n.t('vttnPaid')}</p>
              </div>
            </div>
          </div>

          {/* 추천 안내 */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-green-800">{i18n.t('recommendToFriend')}</p>
                <p className="text-sm text-green-600">{i18n.t('bothGetBonus')}</p>
              </div>
            </div>
          </div>

          {/* 웰컴보너스 받기 버튼 - 추천코드 입력으로 연결 */}
          <Button
            onClick={onWelcomeBonusClick}
            className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg"
          >
            <Gift className="w-5 h-5 mr-2" />
            {i18n.t('getWelcomeBonus')}
          </Button>

          {/* 액션 버튼들 */}
          <div className="flex space-x-3 pt-2">
            <Button
              onClick={handleDontShowAgain}
              variant="outline"
              className="flex-1 h-10 border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl"
            >
              <Check className="w-4 h-4 mr-2" />
              {i18n.t('dontShowAgain')}
            </Button>
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1 h-10 border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl"
            >
              {i18n.t('close')}
            </Button>
          </div>
        </div>

        {/* 하단 정보 */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {i18n.t('discoverMoreBenefits')}
          </p>
        </div>
      </div>
    </div>
  );
}
